import React, { Component } from 'react';

import AuthContext from '../../contexts/AuthContext';
import socket from '../../utils/socket';
import Peer from 'peerjs';
import { getEmojiRecommendations } from '../../utils/sideEffects';

import ClosedCaption from './ClosedCaption';
import ChatWindow from '../../components/Message/ChatWindow';
import NewMessage from '../../components/Message/NewMessage';
import VideoChatButton from '../../components/VideoChat/VideoChatButton';
import Dialog from '../../components/UI/Dialog';

class VideoChat extends Component {

    state = {
        messages: [],
        myVideoStream: null,
        remoteUserStream: null,
        remoteUser: null,
        transcriptText: '',
        error: null,
        isLoading: false,
        isAudioEnabled: true,
        isVideoEnabled: true,
        isCCEnabled: false,
        isAddFriendEnabled: false,
        emojiRecommendations: null,
        showAddFriend: false,
        dialog: {
            show: false,
            title: 'Title',
            content: 'Content',
            type: 'alert'
        }
    }

    constructor(props) {
        super(props);
        this.myVideoRef = React.createRef();
        this.userVideoRef = React.createRef();
    }

    static contextType = AuthContext;

    getPermissions = async (callback) => {
        try {
            const stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
            this.myVideoRef.current.srcObject = stream;
            this.myVideoRef.current.muted = true;
            this.setState({ myVideoStream: stream });
            callback(null);
        }
        catch (error) {
            this.setState({ error: error });
            callback(error);
        }
    }

    microphoneHandler = () => {
        let isAudioEnabled = this.state.myVideoStream.getAudioTracks()[0].enabled;
        if (isAudioEnabled) {
            isAudioEnabled = false;
        } else {
            isAudioEnabled = true;
        }
        this.state.myVideoStream.getAudioTracks()[0].enabled = isAudioEnabled;
        this.setState({ isAudioEnabled: isAudioEnabled });
    }

    videoHandler = () => {
        let isVideoEnabled = this.state.myVideoStream.getVideoTracks()[0].enabled;
        if (isVideoEnabled) {
            isVideoEnabled = false;
        } else {
            isVideoEnabled = true;
        }
        this.state.myVideoStream.getVideoTracks()[0].enabled = isVideoEnabled;
        this.setState({ isVideoEnabled: isVideoEnabled });
    }

    closedCaptionHandler = () => {
        this.setState(prevState => { return { isCCEnabled: !prevState.isCCEnabled } });
    }

    addFriendHandler = () => {
        socket.emit('addFriend', { remoteUserId: this.state.remoteUser.userId });
        this.setState({
            dialog: {
                title: 'Friend Request Sent!',
                content: 'You sent friend request to ' + this.state.remoteUser.firstName,
                type: 'alert',
                show: true
            }
        });
    }

    firendRequestHandler = () => {
        this.setState({
            dialog: {
                title: 'Friend Request',
                content: this.state.remoteUser.firstName + ' wants to add you as a friend. Accept?',
                type: 'confirm',
                show: true
            }
        });
    }

    friendRequestAcceptHandler = () => {
        if (this.state.dialog.type === 'confirm')
            socket.emit('friendRequestAccepted', { userId: this.context.userId, remoteUserId: this.state.remoteUser.userId });
        this.setState({
            isAddFriendEnabled: false,
            dialog: {
                title: 'Friend Request Accepted!',
                content: this.state.remoteUser.firstName + ' is now added to your friends list.',
                type: 'alert',
                show: true
            }
        });
    }

    friendRequestCancelledHandler = () => {
        this.setState({
            dialog: {
                title: 'Friend Request Declined!',
                content: this.state.remoteUser.firstName + ' declined your friend request.',
                type: 'alert',
                show: true
            }
        });
    }

    dialogCloseHandler = () => {
        if (this.state.dialog.type === 'confirm') {
            socket.emit('friendRequestDeclined', { remoteUserId: this.state.remoteUser.userId });
        }
        this.setState(prevState => {
            return {
                dialog: {
                    ...prevState.dialog,
                    ['show']: false
                }
            }
        });
    }

    endCallHandler = () => {
        socket.emit('endCall', { userId: this.context.userId });
        if (this.state.myVideoStream) {
            const myVideoTracks = this.state.myVideoStream.getTracks();
            myVideoTracks.forEach(track => track.stop());
        }
        if (this.state.remoteUserStream) {
            const remoteVideoTracks = this.state.remoteUserStream.getTracks();
            remoteVideoTracks.forEach(track => track.stop());
        }
        this.props.history.replace('/');
    }

    sendMessageHandler = (messageText) => {
        const message = {
            from: {
                userId: this.context.userId,
                firstName: this.context.firstName,
                lastName: this.context.lastName
            },
            to: this.state.remoteUser,
            text: messageText,
        }
        this.setState(prevState => { return { messages: [...prevState.messages, message] } });
        socket.emit('sendMessageOnVC', { message: message });
    }

    emojiRecommendationClickHandler = async (event, message) => {
        event.preventDefault();
        const result = await getEmojiRecommendations(message);
        if (result.status !== 200)
            this.setState({ error: result.data });
        else {
            this.setState({ emojiRecommendations: result.data.recommendations.map(r => r.toString(16)) });
        }
    }

    clearEmojiRecommendationHadler = () => {
        this.setState({ emojiRecommendations: null });
    }

    componentDidMount() {
        this.getPermissions((error) => {
            if (error) return console.log('Permission not granted!');
            const peer = new Peer('', {
                port: 443
            });
            peer.on('open', (id) => {
                socket.emit('joinVideoCall', { peerId: id, userId: this.context.userId });
            });
            // On Incoming Call, Answer the call with My Video Stream
            peer.on('call', call => {
                call.answer(this.state.myVideoStream);
                call.on('stream', remoteUserStream => {
                    this.userVideoRef.current.srcObject = remoteUserStream;
                    this.setState({ remoteUserStream: remoteUserStream, isAddFriendEnabled: true, showAddFriend: true });
                });
            });
            // On Match, Call the User with My Video Stream
            socket.on('videoCallMatchFound', ({ remoteUser }) => {
                const call = peer.call(remoteUser.peerId, this.state.myVideoStream);
                call.on('stream', remoteUserStream => {
                    this.userVideoRef.current.srcObject = remoteUserStream;
                    this.setState({ remoteUserStream: remoteUserStream, remoteUser: remoteUser, isAddFriendEnabled: true, showAddFriend: true });
                });
            });
            // On Match, Store Matched User Data
            socket.on('remoteUserMatched', ({ remoteUser }) => {
                this.setState({ remoteUser: remoteUser });
            });
            socket.on('messageOnVC', ({ message }) => {
                this.setState(prevState => { return { messages: [...prevState.messages, message] } });
            });
            socket.on('friendRequest', this.firendRequestHandler);
            socket.on('friendsAdded', this.friendRequestAcceptHandler);
            socket.on('friendRequestCancelled', this.friendRequestCancelledHandler);
            socket.on('callEnded', () => {
                const myVideoTracks = this.state.myVideoStream.getTracks();
                myVideoTracks.forEach(track => track.stop());
                const remoteVideoTracks = this.state.remoteUserStream.getTracks();
                remoteVideoTracks.forEach(track => track.stop());
                this.props.history.replace('/');
            });
        });
    }

    componentWillUnmount() {
        this.endCallHandler();
        socket.off('joinVideoCall');
        socket.off('videoCallMatchFound');
        socket.off('remoteUserMatched');
        socket.off('messageOnVC');
        socket.off('friendRequest');
        socket.off('friendsAdded');
        socket.off('friendRequestCancelled');
        socket.off('callEnded');
    }

    render() {
        return (
            <div className="video-chat">
                <Dialog
                    show={this.state.dialog.show}
                    type={this.state.dialog.type}
                    title={this.state.dialog.title}
                    clicked={this.state.dialog.type === 'confirm' && this.friendRequestAcceptHandler}
                    closed={this.dialogCloseHandler}
                >
                    {this.state.dialog.content}
                </Dialog>
                <div className="video-chat__window">
                    <div className="video-chat__users">
                        <video className="video-chat__user" ref={this.userVideoRef} onLoadedMetadata={() => { this.userVideoRef.current.play() }} ></video>
                        <video className="video-chat__user" ref={this.myVideoRef} onLoadedMetadata={() => { this.myVideoRef.current.play() }} ></video>
                    </div>
                    < ClosedCaption isEnabled={this.state.isCCEnabled} />
                    <div className="video-chat__actions">
                        <VideoChatButton isOff={!this.state.isAudioEnabled} btnOnClass="fa fa-microphone" btnOffClass="fa fa-microphone-slash" clicked={this.microphoneHandler} />
                        <VideoChatButton isOff={!this.state.isVideoEnabled} btnOnClass="fas fa-video" btnOffClass="fas fa-video-slash" clicked={this.videoHandler} />
                        <VideoChatButton isOff={!this.state.isCCEnabled} btnOnClass="fas fa-closed-captioning" btnOffClass="far fa-closed-captioning" clicked={this.closedCaptionHandler} />
                        {this.state.showAddFriend && <VideoChatButton disabled={!this.state.isAddFriendEnabled} isOff={!this.state.isAddFriendEnabled} btnOnClass="fas fa-user-plus" btnOffClass="fas fa-user-check" clicked={this.addFriendHandler} />}
                        <button className="video-chat__btn video-chat__btn--end" onClick={this.endCallHandler}><i className="fas fa-phone"></i></button>
                    </div>
                </div>
                <div className="video-chat__messages">
                    <ChatWindow
                        isUserSelected={true}
                        isLoading={false}
                        messages={this.state.messages}
                        currentUser={this.context.userId} />
                    <NewMessage
                        send={this.sendMessageHandler}
                        emojiRecommendations={this.state.emojiRecommendations}
                        clicked={this.emojiRecommendationClickHandler}
                        cleared={this.clearEmojiRecommendationHadler} />
                </div>
            </div>
        );
    }
}

export default VideoChat;